/**
 * @module bridges
 * @package @geenius/adapters/solidjs-kobalte
 * @description Kobalte-backed SolidJS bridges for async adapter loading,
 * adapter error fallbacks, and telemetry toast notifications.
 */

import type { JSX } from "solid-js";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  ErrorBoundary,
  For,
  onCleanup,
  Show,
} from "solid-js";

type BridgeTone = "info" | "success" | "warning" | "error";
type BridgeSize = "sm" | "md" | "lg";

function cn(
  ...classes: Array<string | false | null | undefined>
): string | undefined {
  const value = classes.filter(Boolean).join(" ");
  return value.length > 0 ? value : undefined;
}

function accessibleText(value: JSX.Element, fallback: string): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return fallback;
}

const toneClasses: Record<BridgeTone, string> = {
  info: "border-sky-500/30 bg-sky-50 text-sky-950",
  success: "border-emerald-500/30 bg-emerald-50 text-emerald-950",
  warning: "border-amber-500/40 bg-amber-50 text-amber-950",
  error: "border-red-500/40 bg-red-50 text-red-950",
};

const loadingSizeClasses: Record<BridgeSize, string> = {
  sm: "gap-2 p-3 text-xs",
  md: "gap-3 p-4 text-sm",
  lg: "gap-4 p-5 text-base",
};

type KobalteCore = typeof import("@kobalte/core");

let kobalteCorePromise: Promise<KobalteCore | undefined> | undefined;

function loadKobalteCore(): Promise<KobalteCore | undefined> {
  kobalteCorePromise ??= import("@kobalte/core").catch(() => undefined);
  return kobalteCorePromise;
}

export interface LoadingBridgeProps {
  /** Main loading label announced through the Kobalte progress primitive. */
  label?: JSX.Element;
  /** Optional supporting copy rendered under the label. */
  description?: JSX.Element;
  /** Determinate progress value. Omit for an indeterminate loading state. */
  value?: number;
  /** Maximum determinate value. */
  maxValue?: number;
  /** Visual density of the loading affordance. */
  size?: BridgeSize;
  /** Additional Tailwind classes for the Kobalte progress root. */
  class?: string;
  /** Stable test selector for consumers that need one. */
  testId?: string;
}

/** Kobalte progress bridge for async adapter loading states. */
export function LoadingBridge(props: LoadingBridgeProps): JSX.Element {
  const [core] = createResource(loadKobalteCore);
  const label = createMemo(() => props.label ?? "Loading adapters");
  const accessibleLabel = createMemo(() =>
    accessibleText(label(), "Loading adapters"),
  );
  const maxValue = createMemo(() => props.maxValue ?? 100);
  const isIndeterminate = createMemo(() => props.value === undefined);
  const rootClass = createMemo(() =>
    cn(
      "grid rounded-md border border-slate-200 bg-white text-slate-950 shadow-sm",
      loadingSizeClasses[props.size ?? "md"],
      props.class,
    ),
  );

  return (
    <Show
      fallback={
        <div
          aria-label={accessibleLabel()}
          aria-valuemax={maxValue()}
          aria-valuemin={0}
          aria-valuenow={isIndeterminate() ? undefined : props.value}
          class={rootClass()}
          data-geenius-bridge="loading"
          data-testid={props.testId}
          role="progressbar"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-medium leading-none">{label()}</span>
            <Show when={!isIndeterminate()}>
              <span class="text-xs text-slate-500">{props.value}%</span>
            </Show>
          </div>
          <div class="h-2 overflow-hidden rounded-full bg-slate-200">
            <div
              class="h-full rounded-full bg-slate-950 transition-[width]"
              style={{
                width: isIndeterminate() ? "0%" : `${props.value ?? 0}%`,
              }}
            />
          </div>
          <Show when={props.description}>
            <p class="text-xs leading-snug text-slate-500">
              {props.description}
            </p>
          </Show>
        </div>
      }
      keyed
      when={core()?.Progress}
    >
      {(Progress) => (
        <Progress.Root
          class={rootClass()}
          data-geenius-bridge="loading"
          data-testid={props.testId}
          indeterminate={isIndeterminate()}
          maxValue={maxValue()}
          value={props.value}
        >
          <div class="flex items-center justify-between gap-3">
            <Progress.Label class="font-medium leading-none">
              {label()}
            </Progress.Label>
            <Show when={!isIndeterminate()}>
              <Progress.ValueLabel class="text-xs text-slate-500" />
            </Show>
          </div>
          <Progress.Track class="h-2 overflow-hidden rounded-full bg-slate-200">
            <Progress.Fill class="h-full rounded-full bg-slate-950 transition-[width]" />
          </Progress.Track>
          <Show when={props.description}>
            <p class="text-xs leading-snug text-slate-500">
              {props.description}
            </p>
          </Show>
        </Progress.Root>
      )}
    </Show>
  );
}

type ErrorBoundaryFallback =
  | JSX.Element
  | ((error: Error, reset: () => void) => JSX.Element);

export interface ErrorBoundaryBridgeProps {
  /** Descendant tree guarded by the SolidJS error boundary. */
  children?: JSX.Element;
  /** Explicit adapter error to render without waiting for a descendant throw. */
  error?: unknown;
  /** Optional custom fallback node or renderer. */
  fallback?: ErrorBoundaryFallback;
  /** Heading used by the default Kobalte alert fallback. */
  title?: JSX.Element;
  /** Supporting copy used by the default fallback. */
  description?: JSX.Element;
  /** Label for the default reset action. */
  resetLabel?: JSX.Element;
  /** Additional Tailwind classes for the Kobalte alert root. */
  class?: string;
  /** Called when a descendant throws. */
  onError?: (error: Error) => void;
  /** Called before local error state is reset. */
  onReset?: () => void;
  /** Stable test selector for consumers that need one. */
  testId?: string;
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string" && error.length > 0) {
    return new Error(error);
  }

  return new Error("Unknown adapter bridge error");
}

function DefaultErrorFallback(props: {
  error: Error;
  title: JSX.Element;
  description: JSX.Element;
  resetLabel: JSX.Element;
  onReset: () => void;
  class?: string;
  testId?: string;
}): JSX.Element {
  const [core] = createResource(loadKobalteCore);
  const rootClass = createMemo(() =>
    cn("rounded-md border p-4 shadow-sm", toneClasses.error, props.class),
  );
  const body = () => (
    <div class="grid gap-2">
      <div class="text-sm font-semibold leading-none">{props.title}</div>
      <p class="text-sm leading-snug opacity-90">{props.description}</p>
      <p class="break-words text-xs opacity-80">{props.error.message}</p>
      <button
        class="inline-flex h-8 w-fit items-center justify-center rounded-md border border-current/20 bg-white/60 px-3 text-xs font-medium transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/30"
        onClick={props.onReset}
        type="button"
      >
        {props.resetLabel}
      </button>
    </div>
  );

  return (
    <Show
      fallback={
        <div
          class={rootClass()}
          data-geenius-bridge="error-boundary"
          data-testid={props.testId}
          role="alert"
        >
          {body()}
        </div>
      }
      keyed
      when={core()?.Alert}
    >
      {(Alert) => (
        <Alert.Root
          class={rootClass()}
          data-geenius-bridge="error-boundary"
          data-testid={props.testId}
        >
          {body()}
        </Alert.Root>
      )}
    </Show>
  );
}

/** SolidJS error boundary rendered with Kobalte's alert primitive. */
export function ErrorBoundaryBridge(
  props: ErrorBoundaryBridgeProps,
): JSX.Element {
  const explicitError = createMemo(() => props.error);
  const renderFallback = (errorInput: unknown, reset: () => void) => {
    const error = normalizeError(errorInput);
    const resetAndNotify = () => {
      props.onReset?.();
      reset();
    };

    if (typeof props.fallback === "function") {
      return props.fallback(error, resetAndNotify);
    }

    if (props.fallback !== undefined) {
      return props.fallback;
    }

    return (
      <DefaultErrorFallback
        class={props.class}
        description={
          props.description ??
          "The adapter bridge could not finish the requested work."
        }
        error={error}
        onReset={resetAndNotify}
        resetLabel={props.resetLabel ?? "Try again"}
        testId={props.testId}
        title={props.title ?? "Adapter error"}
      />
    );
  };

  return (
    <Show
      fallback={
        <ErrorBoundary
          fallback={(error, reset) => {
            const normalized = normalizeError(error);
            props.onError?.(normalized);
            return renderFallback(normalized, reset);
          }}
        >
          {props.children}
        </ErrorBoundary>
      }
      keyed
      when={explicitError()}
    >
      {(error) => renderFallback(error, () => undefined)}
    </Show>
  );
}

export type ToastBridgeVariant = "default" | "success" | "warning" | "error";

export type ToastBridgeSwipeDirection = "up" | "down" | "left" | "right";

export interface ToastBridgeNotification {
  /** Stable source id used to avoid duplicate notification dispatches. */
  id: string;
  /** Toast title rendered through Kobalte Toast.Title. */
  title: JSX.Element;
  /** Optional supporting message rendered through Kobalte Toast.Description. */
  description?: JSX.Element;
  /** Visual and assistive priority treatment. */
  variant?: ToastBridgeVariant;
  /** Auto-dismiss timeout for this notification. */
  duration?: number;
  /** Whether this toast remains until dismissed. */
  persistent?: boolean;
}

export interface ToastBridgeProps {
  /** Notifications to enqueue into Kobalte's toaster. */
  notifications?: readonly ToastBridgeNotification[];
  /** Kobalte region id used when multiple toast regions exist. */
  regionId?: string;
  /** Default auto-dismiss timeout in milliseconds. */
  duration?: number;
  /** Maximum simultaneously visible toasts. */
  limit?: number;
  /** Kobalte swipe direction for dismiss gestures. */
  swipeDirection?: ToastBridgeSwipeDirection;
  /** Clears all Kobalte toasts when this bridge unmounts. */
  clearOnUnmount?: boolean;
  /** Called after a notification is handed to Kobalte's toaster. */
  onNotify?: (event: { id: string; toastId: number }) => void;
  /** Additional Tailwind classes for the Kobalte toast region. */
  class?: string;
  /** Stable test selector for consumers that need one. */
  testId?: string;
}

const toastToneClasses: Record<ToastBridgeVariant, string> = {
  default: "border-slate-200 bg-white text-slate-950",
  success: toneClasses.success,
  warning: toneClasses.warning,
  error: toneClasses.error,
};

type KobalteToastCore = KobalteCore & {
  Toast: NonNullable<KobalteCore["Toast"]>;
  toaster: NonNullable<KobalteCore["toaster"]>;
};

function hasKobalteToastCore(
  core: KobalteCore | undefined,
): core is KobalteToastCore {
  return (
    core?.Toast !== undefined &&
    typeof core.toaster?.clear === "function" &&
    typeof core.toaster.show === "function"
  );
}

function uniqueNotifications(
  notifications: readonly ToastBridgeNotification[],
): ToastBridgeNotification[] {
  const seen = new Set<string>();
  const unique: ToastBridgeNotification[] = [];

  for (const notification of notifications) {
    if (seen.has(notification.id)) {
      continue;
    }

    seen.add(notification.id);
    unique.push(notification);
  }

  return unique;
}

function toastPriority(
  variant: ToastBridgeVariant | undefined,
): "high" | "low" {
  return variant === "error" || variant === "warning" ? "high" : "low";
}

/** Kobalte toast region and enqueue bridge for adapter telemetry events. */
export function ToastBridge(props: ToastBridgeProps): JSX.Element {
  const [core] = createResource(loadKobalteCore);
  const [fallbackReady, setFallbackReady] = createSignal(false);
  const notifications = createMemo(() =>
    uniqueNotifications(props.notifications ?? []),
  );
  const enqueuedNotificationIds = new Set<string>();
  const fallbackNotifiedIds = new Set<string>();
  const kobalteNotifiedIds = new Set<string>();
  let clearToasts: (() => void) | undefined;
  const fallbackTimer = setTimeout(() => {
    setFallbackReady(true);
  }, 0);

  createEffect(() => {
    const loadedCore = core();
    if (!hasKobalteToastCore(loadedCore)) {
      if (!fallbackReady()) {
        return;
      }

      for (const notification of notifications()) {
        if (fallbackNotifiedIds.has(notification.id)) {
          continue;
        }

        fallbackNotifiedIds.add(notification.id);
        props.onNotify?.({ id: notification.id, toastId: -1 });
      }

      return;
    }

    for (const notification of notifications()) {
      if (enqueuedNotificationIds.has(notification.id)) {
        continue;
      }

      enqueuedNotificationIds.add(notification.id);
      const variant = notification.variant ?? "default";
      const { Toast, toaster } = loadedCore;
      clearToasts = toaster.clear;
      const toastId = toaster.show(
        (toastProps) => (
          <Toast.Root
            class={cn(
              "pointer-events-auto grid gap-1 rounded-md border p-4 shadow-lg",
              toastToneClasses[variant],
            )}
            duration={notification.duration ?? props.duration}
            persistent={notification.persistent}
            priority={toastPriority(notification.variant)}
            toastId={toastProps.toastId}
          >
            <Toast.Title class="text-sm font-semibold leading-none">
              {notification.title}
            </Toast.Title>
            <Show when={notification.description}>
              <Toast.Description class="text-sm leading-snug opacity-90">
                {notification.description}
              </Toast.Description>
            </Show>
            <Toast.CloseButton
              aria-label="Dismiss notification"
              class="mt-2 inline-flex h-7 w-fit items-center justify-center rounded-md border border-current/20 bg-white/50 px-2 text-xs font-medium"
            >
              Dismiss
            </Toast.CloseButton>
          </Toast.Root>
        ),
        { region: props.regionId },
      );

      if (!kobalteNotifiedIds.has(notification.id)) {
        kobalteNotifiedIds.add(notification.id);
        props.onNotify?.({ id: notification.id, toastId });
      }
    }
  });

  onCleanup(() => {
    clearTimeout(fallbackTimer);
    if (props.clearOnUnmount) {
      clearToasts?.();
    }
  });

  return (
    <Show
      fallback={
        <section
          aria-label="Adapter notifications"
          class={cn(
            "fixed right-4 top-4 z-50 grid w-96 max-w-[calc(100vw-2rem)] gap-2",
            props.class,
          )}
          data-geenius-bridge="toast"
          data-testid={props.testId}
        >
          <For each={notifications()}>
            {(notification) => (
              <div
                class={cn(
                  "pointer-events-auto grid gap-1 rounded-md border p-4 shadow-lg",
                  toastToneClasses[notification.variant ?? "default"],
                )}
                role={notification.variant === "error" ? "alert" : "status"}
              >
                <div class="text-sm font-semibold leading-none">
                  {notification.title}
                </div>
                <Show when={notification.description}>
                  <div class="text-sm leading-snug opacity-90">
                    {notification.description}
                  </div>
                </Show>
              </div>
            )}
          </For>
        </section>
      }
      keyed
      when={hasKobalteToastCore(core()) ? core()?.Toast : undefined}
    >
      {(Toast) => (
        <Toast.Region
          class={cn(
            "fixed right-4 top-4 z-50 grid w-96 max-w-[calc(100vw-2rem)] gap-2",
            props.class,
          )}
          data-geenius-bridge="toast"
          data-testid={props.testId}
          duration={props.duration}
          limit={props.limit}
          regionId={props.regionId}
          swipeDirection={props.swipeDirection}
        >
          <Toast.List class="grid gap-2 outline-none" />
        </Toast.Region>
      )}
    </Show>
  );
}
